import { useTranslation } from 'react-i18next';

const contracts = [
  {
    name: 'STARTER',
    amount: '3 000 €',
    profit: '20%',
    profitEur: '600 €',
    popular: false,
    featureKeys: [
      'contracts.starter.f1', 'contracts.starter.f2', 'contracts.starter.f3',
      'contracts.starter.f4', 'contracts.starter.f5', 'contracts.starter.f6',
    ],
    accent: 'from-gray-600 to-gray-500',
    border: 'border-gray-600/30',
    hover: 'hover:border-gray-500/50',
    btn: 'bg-gray-600 hover:bg-gray-500 text-white',
    textColor: 'text-gray-300',
    popularKey: null,
  },
  {
    name: 'ADVANCED',
    amount: '5 000 €',
    profit: '30%',
    profitEur: '1 500 €',
    popular: true,
    featureKeys: [
      'contracts.advanced.f1', 'contracts.advanced.f2', 'contracts.advanced.f3',
      'contracts.advanced.f4', 'contracts.advanced.f5', 'contracts.advanced.f6', 'contracts.advanced.f7',
    ],
    accent: 'from-amber-500 to-orange-500',
    border: 'border-amber-500/50',
    hover: 'hover:border-amber-400',
    btn: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black',
    textColor: 'text-amber-400',
    popularKey: 'contracts.popular',
  },
  {
    name: 'PREMIUM',
    amount: '10 000 €',
    profit: '50%',
    profitEur: '5 000 €',
    popular: false,
    featureKeys: [
      'contracts.premium.f1', 'contracts.premium.f2', 'contracts.premium.f3',
      'contracts.premium.f4', 'contracts.premium.f5', 'contracts.premium.f6',
      'contracts.premium.f7', 'contracts.premium.f8',
    ],
    accent: 'from-orange-400 to-amber-300',
    border: 'border-orange-400/30',
    hover: 'hover:border-orange-400/60',
    btn: 'bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-400 hover:to-amber-300 text-black',
    textColor: 'text-orange-400',
    popularKey: null,
  },
];

export default function ContractsSection() {
  const { t } = useTranslation();

  return (
    <section id="contracts" className="bg-[#080812] py-16 md:py-24 px-4 md:px-6 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-['Rajdhani'] uppercase tracking-widest mb-3 md:mb-4">
            <div className="w-8 h-[1px] bg-amber-400" />
            {t('contracts.tag')}
            <div className="w-8 h-[1px] bg-amber-400" />
          </div>
          <h2 className="font-['Orbitron'] text-white font-black text-3xl md:text-5xl mb-3 md:mb-4">
            {t('contracts.title1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">{t('contracts.title2')}</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-xl mx-auto font-['Rajdhani']">{t('contracts.sub')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 items-start">
          {contracts.map((c) => (
            <div
              key={c.name}
              className={`relative bg-[#0e0e1a] border ${c.border} ${c.hover} rounded-2xl p-6 md:p-8 transition-all duration-300 ${
                c.popular ? 'ring-1 ring-amber-500/30 md:scale-105' : ''
              }`}
            >
              {c.popularKey && (
                <div className={`absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r ${c.accent} text-black text-xs font-black px-4 py-1.5 rounded-full font-['Orbitron'] tracking-wider whitespace-nowrap`}>
                  {t(c.popularKey)}
                </div>
              )}

              <div className="mb-5 md:mb-6">
                <div className="font-['Orbitron'] text-gray-400 text-xs tracking-widest uppercase mb-2">{c.name}</div>
                <div className={`font-['Orbitron'] font-black text-3xl md:text-4xl text-transparent bg-clip-text bg-gradient-to-r ${c.accent}`}>
                  {c.amount}
                </div>
                <div className="text-gray-500 text-xs md:text-sm font-['Rajdhani'] mt-1">{t('contracts.duration')}</div>
              </div>

              <div className="bg-black/30 border border-white/5 rounded-xl p-3 md:p-4 mb-5 md:mb-6 text-center">
                <div className="text-gray-400 text-xs font-['Rajdhani'] uppercase tracking-widest mb-1">{t('contracts.profitlabel')}</div>
                <div className={`font-['Orbitron'] font-black text-2xl md:text-3xl text-transparent bg-clip-text bg-gradient-to-r ${c.accent}`}>
                  {c.profit}
                </div>
                <div className="text-gray-300 text-xs md:text-sm font-['Rajdhani'] mt-1">
                  +{c.profitEur} {t('contracts.pure')}
                </div>
              </div>

              <ul className="space-y-2.5 md:space-y-3 mb-6 md:mb-8">
                {c.featureKeys.map((fKey) => (
                  <li key={fKey} className="flex items-start gap-2.5 md:gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-amber-400 text-sm"></i>
                    </div>
                    <span className="text-gray-400 text-xs md:text-sm font-['Rajdhani']">{t(fKey)}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => document.querySelector('#profit')?.scrollIntoView({ behavior: 'smooth' })}
                className={`w-full font-['Orbitron'] font-bold text-xs md:text-sm py-3.5 md:py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap tracking-wider ${c.btn}`}
              >
                {t('contracts.btn')}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-600 text-xs font-['Rajdhani'] mt-6 md:mt-8 max-w-2xl mx-auto px-4">
          {t('contracts.disclaimer')}
        </p>
      </div>
    </section>
  );
}